import type { FieldSelection, RelationQuery } from '#layers/data-model';
import { isRelationQuery } from '#layers/data-model';

/**
 * Merge multiple apiName-based FieldSelections into one.
 *
 * - Primitive strings are unioned (deduplicated).
 * - `'*'` absorbs all primitives but preserves relation entries.
 * - Relation entries for the same apiName are merged recursively.
 * - RelationQuery options: limit → undefined (no limit) wins, else max; filter → first non-undefined; sort → first non-empty.
 */
export function mergeFieldSelections(...selections: FieldSelection[]): FieldSelection {
	if (selections.length === 0) return [];
	if (selections.length === 1) return selections[0]!;

	const primitives = new Set<string>();
	let hasWildcard = false;

	// Map from relation apiName → collected values (each either FieldSelection or RelationQuery)
	const relations = new Map<string, Array<FieldSelection | RelationQuery>>();

	for (const selection of selections) {
		for (const entry of selection) {
			if (typeof entry === 'string') {
				if (entry === '*') {
					hasWildcard = true;
				}
				else {
					primitives.add(entry);
				}
			}
			else {
				// Object entry — each key is a relation apiName
				for (const [apiName, value] of Object.entries(entry)) {
					let existing = relations.get(apiName);

					if (!existing) {
						existing = [];
						relations.set(apiName, existing);
					}

					existing.push(value);
				}
			}
		}
	}

	const result: FieldSelection = [];

	// Primitives: '*' absorbs individual primitives
	if (hasWildcard) {
		result.push('*');
	}
	else {
		for (const p of primitives) {
			result.push(p);
		}
	}

	// Relations: merge collected values per apiName
	for (const [apiName, values] of relations) {
		result.push({ [apiName]: mergeRelationValues(values) });
	}

	return result;
}

function mergeRelationValues(values: Array<FieldSelection | RelationQuery>): FieldSelection | RelationQuery {
	if (values.length === 1) return values[0]!;

	// Normalize: separate into RelationQuery entries and FieldSelection entries
	const queries: RelationQuery[] = [];
	const fieldSelections: FieldSelection[] = [];

	for (const value of values) {
		if (isRelationQuery(value)) {
			queries.push(value);
		}
		else {
			fieldSelections.push(value);
		}
	}

	// All are plain FieldSelection arrays — merge fields recursively
	if (queries.length === 0) {
		return mergeFieldSelections(...fieldSelections);
	}

	// Convert any shorthand FieldSelection arrays to RelationQuery with just fields
	for (const fs of fieldSelections) {
		queries.push({ fields: fs });
	}

	// Merge all RelationQuery entries
	return mergeRelationQueries(queries);
}

function mergeRelationQueries(queries: RelationQuery[]): RelationQuery {
	// Merge fields recursively
	const allFields = queries.map((q) => q.fields);
	const mergedFields = mergeFieldSelections(...allFields);

	// Limit: undefined (no limit) is most permissive; if all defined, take max
	let limit: number | undefined;
	const hasNoLimit = queries.some((q) => q.limit === undefined);

	if (!hasNoLimit) {
		limit = Math.max(...queries.map((q) => q.limit!));
	}

	// Filter: first non-undefined wins (not merged in PR1)
	const filter = queries.find((q) => q.filter !== undefined)?.filter;

	// Sort: first non-empty wins
	const sort = queries.find((q) => q.sort && q.sort.length > 0)?.sort;

	// Offset: first non-undefined wins
	const offset = queries.find((q) => q.offset !== undefined)?.offset;

	const result: RelationQuery = { fields: mergedFields };

	if (limit !== undefined) result.limit = limit;
	if (filter !== undefined) result.filter = filter;
	if (sort !== undefined) result.sort = sort;
	if (offset !== undefined) result.offset = offset;

	return result;
}
